/**
 * Relacion a varios registros (`many2many`), dibujada como chips.
 *
 * Los chips ganan al clasico "select multiple" por una razon concreta: muestran
 * lo elegido sin scrollear y se quitan de a uno sin riesgo de perder el resto de
 * la seleccion con un click distraido.
 *
 * El backend manda solo los ids, asi que los nombres se resuelven con una
 * consulta aparte. Mientras llega, el chip muestra el id: es feo por medio
 * segundo, pero no miente sobre cuantos elementos hay.
 */

import { useQuery } from '@tanstack/react-query';

import { toIdList } from '@/components/fields/format';
import { RelationPicker } from '@/components/fields/RelationPicker';
import type { FieldInputProps } from '@/components/fields/types';
import { IconClose } from '@/components/shell/icons';
import { api } from '@/lib/api';
import type { Domain } from '@/lib/types';
import { useSessionStore } from '@/store/session';

export function Many2ManyField({
  id,
  meta,
  value,
  onChange,
  readonly,
  required,
  invalid,
  describedBy,
}: FieldInputProps) {
  const ids = toIdList(value);
  const companyId = useSessionStore((estado) => estado.activeCompanyId);
  const relation = meta.relation;

  const nombres = useQuery({
    queryKey: ['m2m-labels', companyId, relation ?? '', ids],
    queryFn: ({ signal }) => {
      const domain: Domain = [['id', 'in', ids]];
      return api.searchRead(relation as string, { domain, limit: ids.length }, signal);
    },
    enabled: relation !== undefined && ids.length > 0,
    staleTime: 60_000,
  });

  const etiquetas = new Map<number, string>(
    (nombres.data?.results ?? []).map((registro) => [registro.id, registro.display_name]),
  );

  if (relation === undefined) {
    return (
      <p id={id} className="text-sm text-danger">
        El campo no declara a qué modelo apunta.
      </p>
    );
  }

  const quitar = (idQuitado: number): void => {
    onChange(ids.filter((actual) => actual !== idQuitado));
  };

  return (
    <div
      id={id}
      role="group"
      aria-labelledby={`${id}-label`}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
    >
      {ids.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5" aria-label={meta.label}>
          {ids.map((idElegido) => (
            <li
              key={idElegido}
              className="inline-flex items-center gap-1 rounded-full border border-accent/25 bg-accent-soft py-0.5 pl-2.5 pr-1 text-xs font-medium text-accent"
            >
              <span className="max-w-[16rem] truncate">{etiquetas.get(idElegido) ?? `#${idElegido}`}</span>
              {!readonly && (
                <button
                  type="button"
                  onClick={() => {
                    quitar(idElegido);
                  }}
                  className="grid h-5 w-5 place-items-center rounded-full transition-colors hover:bg-accent/15"
                  aria-label={`Quitar ${etiquetas.get(idElegido) ?? `#${idElegido}`}`}
                >
                  <IconClose className="h-3 w-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {readonly ? (
        ids.length === 0 && <p className="text-sm text-fg-faint">—</p>
      ) : (
        <RelationPicker
          id={`${id}-picker`}
          relation={relation}
          excludeIds={ids}
          required={required && ids.length === 0}
          invalid={invalid}
          placeholder={ids.length === 0 ? `Agregar ${meta.label.toLowerCase()}…` : 'Agregar otro…'}
          onPick={(registro) => {
            if (!ids.includes(registro.id)) onChange([...ids, registro.id]);
          }}
        />
      )}
    </div>
  );
}
